import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { portalApi, apiError } from "../lib/api";
import { usePortalAuth } from "../context/PortalAuth";
import { Sparkles, Mail, KeyRound, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useEffect } from "react";

export default function PortalLogin() {
  const nav = useNavigate();
  const { customer, setSession } = usePortalAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (customer) nav("/portal/home"); }, [customer, nav]);

  const requestLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/portal/auth/request-link", { email });
      setSent(true);
      toast.success("Falls registriert, wurde ein Link & Code gesendet.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await portalApi.post("/portal/auth/verify-code", { email, code });
      setSession(r.data);
      nav("/portal/home");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await portalApi.post("/portal/auth/login", { email, password });
      setSession(r.data);
      nav("/portal/home");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <div className="max-w-md w-full mx-auto flex-1 flex flex-col justify-center px-5 py-10">
        <button onClick={() => nav("/")} className="self-start mb-6 text-stone-400 hover:text-stone-600 flex items-center gap-1 text-sm"><ArrowLeft className="h-4 w-4" /> Zurück</button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 text-[#C59B6C]">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs tracking-[0.3em] font-semibold text-[#4A1D24]">VELOURS &amp; SILK</span>
          </div>
          <h1 className="font-serif text-4xl mt-4 text-stone-900">Mein Konto</h1>
          <p className="text-sm text-stone-500 mt-2">Melden Sie sich sicher an, um Ihre Punkte &amp; Prämien zu sehen.</p>
        </div>

        <div className="p-6 rounded-3xl bg-white border border-stone-200/80 shadow-lg space-y-4" data-testid="portal-login-card">
          {!sent ? (
            <form onSubmit={requestLink} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-stone-500">E-Mail-Adresse</Label>
                <Input data-testid="magic-link-login-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" placeholder="ihre@email.de" />
              </div>
              <Button data-testid="magic-link-submit-button" type="submit" disabled={busy} className="w-full bg-[#4A1D24] hover:bg-[#3a161c] text-white">
                <Mail className="h-4 w-4 mr-2" /> {busy ? "Senden…" : "Anmelde-Link senden"}
              </Button>
              <p className="text-xs text-center text-stone-400">Sie erhalten einen sicheren Einmal-Link &amp; Code per E-Mail.</p>
            </form>
          ) : (
            <form onSubmit={verifyCode} className="space-y-4">
              <p className="text-sm text-stone-600">Wir haben einen Code an <strong>{email}</strong> gesendet. Bitte hier eingeben:</p>
              <div>
                <Label className="text-xs uppercase tracking-wide text-stone-500">Einmal-Code</Label>
                <Input data-testid="otp-code-input" value={code} onChange={(e) => setCode(e.target.value)} required className="mt-1 tracking-[0.4em] text-center text-lg" maxLength={6} placeholder="000000" />
              </div>
              <Button data-testid="verify-code-button" type="submit" disabled={busy} className="w-full bg-[#4A1D24] hover:bg-[#3a161c] text-white">
                {busy ? "Prüfen…" : "Anmelden"}
              </Button>
              <button type="button" onClick={() => setSent(false)} className="w-full text-xs text-stone-400 hover:text-stone-600">Andere E-Mail verwenden</button>
            </form>
          )}

          <Accordion type="single" collapsible>
            <AccordionItem value="pw" className="border-t border-stone-100">
              <AccordionTrigger data-testid="password-login-toggle" className="text-sm text-stone-500 hover:no-underline">
                <span className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> Mit Passwort anmelden</span>
              </AccordionTrigger>
              <AccordionContent>
                <form onSubmit={passwordLogin} className="space-y-3 pt-1">
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-stone-500">E-Mail</Label>
                    <Input data-testid="pw-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-stone-500">Passwort</Label>
                    <Input data-testid="pw-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" />
                  </div>
                  <Button data-testid="password-login-button" type="submit" disabled={busy} variant="outline" className="w-full">Anmelden</Button>
                </form>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
